import type { SessionInitResponse, CatalogueGame } from '@gp/contracts';
import { GameApi, GameApiError } from './api';
import { hasScene, isPlayable } from './games';
import { COLORS } from './theme';

const hex = (n: number) => '#' + n.toString(16).padStart(6, '0');
const FONT = '"Avenir Next", "Segoe UI", system-ui, sans-serif';

let lobbyRoot: HTMLDivElement | null = null;

export function hideLobby(): void {
  if (lobbyRoot) {
    lobbyRoot.remove();
    lobbyRoot = null;
  }
}

export function showLobby(
  api: GameApi,
  onGameStart: (session: SessionInitResponse) => Promise<void>,
): void {
  hideLobby();

  const root = document.createElement('div');
  lobbyRoot = root;
  Object.assign(root.style, {
    position: 'fixed',
    inset: '0',
    zIndex: '100',
    background: `radial-gradient(circle at 50% 0%, #103c34 0%, ${hex(COLORS.ink)} 65%)`,
    fontFamily: FONT,
    color: hex(COLORS.cream),
    overflow: 'auto',
  });

  const inner = document.createElement('div');
  Object.assign(inner.style, {
    maxWidth: '1100px',
    margin: '0 auto',
    padding: '40px 24px 60px',
  });
  root.appendChild(inner);

  const header = document.createElement('header');
  Object.assign(header.style, { textAlign: 'center', marginBottom: '40px' });

  const title = document.createElement('h1');
  Object.assign(title.style, {
    margin: '0 0 6px',
    fontSize: '36px',
    letterSpacing: '0.12em',
    color: hex(COLORS.gold),
  });
  title.textContent = 'GAME PROVIDER';

  const subtitle = document.createElement('p');
  Object.assign(subtitle.style, {
    margin: '0',
    fontSize: '14px',
    letterSpacing: '0.08em',
    color: hex(COLORS.jade),
    textTransform: 'uppercase',
  });
  subtitle.textContent = 'DEMO LOBBY';
  header.append(title, subtitle);
  inner.appendChild(header);

  const grid = document.createElement('div');
  Object.assign(grid.style, {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
    gap: '20px',
  });
  inner.appendChild(grid);

  const loading = document.createElement('p');
  Object.assign(loading.style, {
    textAlign: 'center',
    color: hex(COLORS.loss),
    fontSize: '16px',
  });
  loading.textContent = 'Loading games…';
  inner.appendChild(loading);

  document.body.appendChild(root);

  void api
    .catalogue()
    .then((res) => {
      loading.remove();
      if (res.games.length === 0) {
        const empty = document.createElement('p');
        Object.assign(empty.style, {
          textAlign: 'center',
          color: hex(COLORS.loss),
          fontSize: '16px',
          padding: '60px 20px',
        });
        empty.textContent = 'No games available right now. Check back later!';
        inner.appendChild(empty);
        return;
      }
      for (const game of res.games) {
        grid.appendChild(makeCard(api, game, onGameStart));
      }
    })
    .catch((err: unknown) => {
      loading.textContent =
        err instanceof GameApiError
          ? `Failed to load catalogue: ${err.message}`
          : 'Could not reach the server.';
      loading.style.color = hex(COLORS.danger);
    });
}

function makeCard(
  api: GameApi,
  game: CatalogueGame,
  onGameStart: (session: SessionInitResponse) => Promise<void>,
): HTMLDivElement {
  const card = document.createElement('div');
  Object.assign(card.style, {
    background: `linear-gradient(180deg, rgba(13,59,51,0.7), rgba(6,17,15,0.85))`,
    border: `1px solid rgba(232,182,76,0.25)`,
    borderRadius: '16px',
    padding: '22px 20px 18px',
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    transition: 'border-color 0.2s, transform 0.2s',
    cursor: 'default',
  });
  card.addEventListener('mouseenter', () => {
    card.style.borderColor = 'rgba(232,182,76,0.6)';
    card.style.transform = 'translateY(-3px)';
  });
  card.addEventListener('mouseleave', () => {
    card.style.borderColor = 'rgba(232,182,76,0.25)';
    card.style.transform = 'none';
  });

  const badges = document.createElement('div');
  Object.assign(badges.style, { display: 'flex', gap: '6px', flexWrap: 'wrap' });

  const typeBadge = makeBadge(game.type, hex(COLORS.jade), hex(COLORS.cream));
  badges.appendChild(typeBadge);
  if (game.isNew) badges.appendChild(makeBadge('NEW', '#2e7d32', '#b9f6ca'));
  if (game.isHot) badges.appendChild(makeBadge('HOT', '#c62828', '#ffcdd2'));
  // Playable, but through the form built from its schema rather than art
  // written for it. Worth saying so before the tile is clicked.
  if (isPlayable(game) && !hasScene(game.gameCode)) {
    badges.appendChild(makeBadge('BASIC UI', '#33474a', '#cfe3e0'));
  }
  card.appendChild(badges);

  const name = document.createElement('h3');
  Object.assign(name.style, {
    margin: '0',
    fontSize: '20px',
    color: hex(COLORS.gold),
    letterSpacing: '0.04em',
  });
  name.textContent = game.name;
  card.appendChild(name);

  const rtp = document.createElement('span');
  Object.assign(rtp.style, {
    fontSize: '12px',
    color: hex(COLORS.loss),
    letterSpacing: '0.05em',
  });
  const rtpPct = game.rtp * 100;
  rtp.textContent = `RTP ${Number.isInteger(rtpPct) ? rtpPct : rtpPct.toFixed(2)}%`;
  card.appendChild(rtp);

  const spacer = document.createElement('div');
  spacer.style.flex = '1';
  card.appendChild(spacer);

  const playBtn = document.createElement('button');
  Object.assign(playBtn.style, {
    marginTop: '8px',
    padding: '10px 0',
    border: '0',
    borderRadius: '999px',
    background: `linear-gradient(180deg, #f0c964, ${hex(COLORS.gold)})`,
    color: '#2a1c00',
    fontFamily: FONT,
    fontWeight: '600',
    fontSize: '14px',
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    cursor: 'pointer',
    width: '100%',
  });
  playBtn.textContent = 'PLAY';

  const error = document.createElement('p');
  Object.assign(error.style, {
    margin: '4px 0 0',
    fontSize: '12px',
    color: hex(COLORS.danger),
    minHeight: '16px',
  });

  /**
   * The catalogue lists what the platform will route, which is a longer list
   * than what this client can draw. A game whose round is shared with other
   * players needs a screen of its own, so its tile is left in place — the
   * game is real and an operator's own UI can play it — but it says so here
   * rather than opening a session it cannot show.
   */
  if (!isPlayable(game)) {
    playBtn.disabled = true;
    playBtn.style.opacity = '0.45';
    playBtn.style.cursor = 'not-allowed';
    playBtn.textContent = 'NO CLIENT';
    error.textContent = 'This build has no UI for this game.';
    card.append(playBtn, error);
    return card;
  }

  playBtn.addEventListener('click', () => {
    playBtn.disabled = true;
    playBtn.style.opacity = '0.6';
    playBtn.style.cursor = 'progress';
    error.textContent = '';

    void api
      .openFunSession({ gameCode: game.gameCode })
      .then((session) => {
        hideLobby();
        return onGameStart(session);
      })
      .catch((err: unknown) => {
        playBtn.disabled = false;
        playBtn.style.opacity = '1';
        playBtn.style.cursor = 'pointer';
        error.textContent =
          err instanceof GameApiError && err.code === 'GAME_DISABLED'
            ? 'Fun play is switched off on this deployment.'
            : err instanceof Error
              ? err.message
              : 'Could not start the game.';
      });
  });

  card.append(playBtn, error);
  return card;
}

function makeBadge(text: string, bg: string, fg: string): HTMLSpanElement {
  const badge = document.createElement('span');
  Object.assign(badge.style, {
    display: 'inline-block',
    padding: '3px 8px',
    borderRadius: '6px',
    fontSize: '11px',
    fontWeight: '700',
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    background: bg,
    color: fg,
  });
  badge.textContent = text;
  return badge;
}
